import React, { useState } from 'react';
import { Mail, Phone, MapPin, Send } from 'lucide-react';
import { companyInfo, services } from '../mockData';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    service: '',
    message: ''
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' or 'error'

  const validateForm = () => {
    const newErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9+\-() ]{10,}$/.test(formData.phone)) {
      newErrors.phone = 'Please enter a valid phone number';
    }

    if (!formData.company.trim()) {
      newErrors.company = 'Company name is required';
    }

    if (!formData.service) {
      newErrors.service = 'Please select a service';
    }

    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    } else if (formData.message.trim().length < 20) {
      newErrors.message = 'Message must be at least 20 characters';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (validateForm()) {
      setIsSubmitting(true);
      setSubmitStatus(null);

      // Prepare form data for Web3Forms
      const formPayload = {
        access_key: '87dac5e8-d8f6-4905-aceb-60a7a9ae428e',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        service: formData.service,
        message: formData.message,
        from_name: 'Vaikuntha Media Contact Form',
        subject: `New Contact Form Submission from ${formData.name}`,
        // Honeypot field for spam protection (will be added to form)
        botcheck: ''
      };

      try {
        const response = await fetch('https://api.web3forms.com/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify(formPayload)
        });

        const result = await response.json();

        if (result.success) {
          console.log('Form submitted successfully:', result);
          setSubmitStatus('success');
          
          // Reset form
          setFormData({
            name: '',
            email: '',
            phone: '',
            company: '',
            service: '',
            message: ''
          });

          // Scroll to top to show success message
          window.scrollTo({ top: 0, behavior: 'smooth' });
        } else {
          console.error('Form submission failed:', result);
          setSubmitStatus('error');
        }

      } catch (error) {
        console.error('Form submission error:', error);
        setSubmitStatus('error');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="page-container">
      {/* Contact Hero */}
      <section className="contact-hero-section">
        <div className="container">
          <h1 className="heading-1">GET IN TOUCH</h1>
          <p className="body-large max-w-4xl">
            Ready to transform your communication strategy?<br />
            Let's start a conversation that turns ideas into meaningful impact.
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="section-padding">
        <div className="container">
          <div className="contact-grid">
            {/* Contact Form */}
            <div className="contact-form-section">
              <h2 className="heading-4 mb-40">SEND US A MESSAGE</h2>

              {/* Success Message */}
              {submitStatus === 'success' && (
                <div style={{
                  background: '#d9fb06',
                  color: '#1a1c1b',
                  padding: '20px',
                  borderRadius: '8px',
                  marginBottom: '30px',
                  fontWeight: '600'
                }}>
                  ✅ Thank you! Your message has been sent successfully. We'll get back to you soon!
                </div>
              )}

              {/* Error Message */}
              {submitStatus === 'error' && (
                <div style={{
                  background: '#ff4444',
                  color: 'white',
                  padding: '20px',
                  borderRadius: '8px',
                  marginBottom: '30px',
                  fontWeight: '600'
                }}>
                  ❌ Sorry, something went wrong. Please try again or email us directly at connect@vaikunthamedia.com
                </div>
              )}

              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">Full Name *</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    className={`form-input ${errors.name ? 'error' : ''}`}
                    placeholder="Enter your full name"
                  />
                  {errors.name && <span className="form-error">{errors.name}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email" className="form-label">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    placeholder="your.email@company.com"
                  />
                  {errors.email && <span className="form-error">{errors.email}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="phone" className="form-label">Phone Number *</label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    className={`form-input ${errors.phone ? 'error' : ''}`}
                    placeholder="+91 98765 43210"
                  />
                  {errors.phone && <span className="form-error">{errors.phone}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="company" className="form-label">Company Name *</label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className={`form-input ${errors.company ? 'error' : ''}`}
                    placeholder="Your company name"
                  />
                  {errors.company && <span className="form-error">{errors.company}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="service" className="form-label">Service of Interest *</label>
                  <select
                    id="service"
                    name="service"
                    value={formData.service}
                    onChange={handleChange}
                    className={`form-select ${errors.service ? 'error' : ''}`}
                  >
                    <option value="">Select a service</option>
                    {services.map((service) => (
                      <option key={service.id} value={service.title}>
                        {service.title}
                      </option>
                    ))}
                    <option value="Multiple Services">Multiple Services</option>
                    <option value="General Inquiry">General Inquiry</option>
                  </select>
                  {errors.service && <span className="form-error">{errors.service}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="message" className="form-label">Message *</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    rows="6"
                    className={`form-textarea ${errors.message ? 'error' : ''}`}
                    placeholder="Tell us about your project, goals, and how we can help..."
                  />
                  {errors.message && <span className="form-error">{errors.message}</span>}
                </div>

                {/* Honeypot field - hidden from humans, visible to bots */}
                <input 
                  type="checkbox" 
                  name="botcheck" 
                  className="hidden" 
                  style={{ display: 'none' }}
                  tabIndex="-1"
                  autoComplete="off"
                />

                <button 
                  type="submit" 
                  className="btn-primary btn-large w-full"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : (
                    <>
                      Send Message <Send size={20} />
                    </>
                  )}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="contact-info-section">
              <h2 className="heading-4 mb-40">CONTACT INFORMATION</h2>
              
              <div className="contact-info-card">
                <h3 className="heading-5">General Inquiries</h3>
                <div className="contact-info-items">
                  <a href={`mailto:${companyInfo.email}`} className="contact-info-item">
                    <Mail size={20} />
                    <span>{companyInfo.email}</span>
                  </a>
                  <a href={`tel:${companyInfo.phone}`} className="contact-info-item">
                    <Phone size={20} />
                    <span>{companyInfo.phone}</span>
                  </a>
                </div>
              </div>

              <div className="contact-info-card">
                <h3 className="heading-5">Key Contacts</h3>
                <div className="contact-info-items">
                  <div className="contact-person">
                    <p className="contact-person-name">Bomsi - Director</p>
                    <a href="mailto:bomsi@vaikunthamedia.com" className="contact-info-item">
                      <Mail size={18} />
                      <span>bomsi@vaikunthamedia.com</span>
                    </a>
                    <a href="tel:+919820172027" className="contact-info-item">
                      <Phone size={18} />
                      <span>+91 982 017 2027</span>
                    </a>
                  </div>

                  <div className="contact-person">
                    <p className="contact-person-name">Rishikesh - Communication Lead</p>
                    <a href="mailto:rishi@vaikunthamedia.com" className="contact-info-item">
                      <Mail size={18} />
                      <span>rishi@vaikunthamedia.com</span>
                    </a>
                    <a href="tel:+919611331976" className="contact-info-item">
                      <Phone size={18} />
                      <span>+91 961 133 1976</span>
                    </a>
                  </div>
                </div>
              </div>

              <div className="contact-info-card">
                <h3 className="heading-5">Business Hours</h3>
                <p className="body-small">
                  Monday - Friday: 9:00 AM - 6:00 PM IST<br />
                  Saturday: 10:00 AM - 2:00 PM IST<br />
                  Sunday: Closed
                </p>
              </div>

              <div className="contact-info-card">
                <h3 className="heading-5">Global Reach</h3>
                <div className="contact-info-item">
                  <MapPin size={20} />
                  <span>Serving India and all major Global Markets</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why Contact Us */}
      <section className="section-padding bg-card">
        <div className="container">
          <h2 className="heading-2 text-center mb-96">WHY WORK WITH US</h2>
          <div className="why-contact-grid">
            <div className="why-contact-item">
              <h3 className="heading-5">Fast Response</h3>
              <p className="body-small">
                We typically respond to all inquiries within 24 hours during business days.
              </p>
            </div>
            <div className="why-contact-item">
              <h3 className="heading-5">Free Consultation</h3>
              <p className="body-small">
                Initial consultation is complimentary to understand your needs and explore solutions.
              </p>
            </div>
            <div className="why-contact-item">
              <h3 className="heading-5">Tailored Solutions</h3>
              <p className="body-small">
                Every proposal is customized to your specific business objectives and challenges.
              </p>
            </div>
            <div className="why-contact-item">
              <h3 className="heading-5">No Obligation</h3>
              <p className="body-small">
                Discussing your project with us carries no obligation - we're here to help.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
